import invariant from 'invariant';
import { useMemo } from 'react';

import { usePageContext, usePageData } from './utils';

import type { ThemeUIStyleObject } from 'theme-ui';
import type { TishmanThemeName, LinkProps } from '@tishman/components';
import type { LogoType } from './components/menu';
import type { MetaProps } from './components/Meta';

/** Props that describe what the logo links to in the `MenuBar`. */
export interface LogoLinkConfig<TState = unknown> {
  /** The path the Logo links to. */
  to: string;
  /** The label for the link (will be a title). */
  label: string;
  /** Optional state to add to location state when the logo is clicked. */
  state?: LinkProps<TState>['state'];
}

/** Props that describe a page-specific CTA, like a 'buy tickets' button. */
export interface PageCtaConfig<TState = unknown> {
  /** The path the CTA links to. */
  to: string;
  /** The label for the CTA. */
  label: string;
  /** The style for the CTA. */
  sx?: ThemeUIStyleObject;
  /** Optional state to add to location state when the CTA is clicked. */
  state?: LinkProps<TState>['state'];
}

/** Props that describe a page-specific navigation item, like a 'back' button. */
export interface PageNavProps<TState = unknown> {
  /** The path the Nav item links to. */
  to: string;
  /** The label for the Nav item. */
  label: string;
  /**
   * Optional link variant to use.
   *
   * For a left nav item, the default variant is `'backArrow'`.
   *
   * For a right nav item, the default variant is `'underline'`.
   */
  variant?: LinkProps<TState>['variant'];
  /** Optional state to add to location state when the nav item is clicked. */
  state?: LinkProps<TState>['state'];
}

/**
 * Page-specific config for the App.
 *
 * **Note**: All values are optional, and pages do not have to
 * define any configuration at all, if the defaults are correct.
 */
export interface PageConfig<
  /** State added to location state when the Logo is clicked. */
  LogoLinkState = unknown,
  /** State added to location state when the CTA is clicked. */
  CtaState = unknown,
  /** State added to location state when the right nav item is clicked. */
  RightNavState = unknown,
  /** State added to location state when the left nav item is clicked. */
  LeftNavState = unknown,
> {
  /**
   * The theme to apply to the `MenuBar`.
   *
   * The default is `"Rock Center"`.
   */
  theme?: TishmanThemeName;
  /**
   * The logo to show in the `MenuBar`.
   *
   * The default is `"Rockefeller Center"`.
   */
  logo?: LogoType;
  /**
   * Optional link to wrap the logo in.
   * If not provided, it defaults to the Rockefeller Center home page.
   * Set to `null` to make the logo not link to anything.
   */
  logoLink?: LogoLinkConfig<LogoLinkState> | null;
  /**
   * An optional short name for this page, .e.g., 'Home', or 'Bar SixtyFive'.
   * This name will appear underneath the logo, replacing any
   * logo type (like the "Rockefeller Center" type under the default logo)
   * that may be present.
   *
   * Set this to `null` to hide the logo type.
   */
  pageName?: string | null;
  /**
   * Optional navigation item to appear on the left side of the `MenuBar`.
   * By default, this will be a back arrow button, but can be modified
   * by passing a `variant`.
   *
   * Note that, if provided, this will _replace_ the Menu toggle button!
   */
  leftNav?: PageNavProps<LeftNavState>;
  /**
   * Optional navigation item to appear on the right side of the `MenuBar`,
   * _before_ the Search toggle button. By default, this is a text link,
   * but can be modified by passing a `variant`.
   *
   * Note: This will be hidden on small screens!
   */
  rightNav?: PageNavProps<RightNavState>;
  /**
   * Optional navigation item to appear on the right side of the `MenuBar`,
   * _after_ the Search toggle Button. Visually, this is a button.
   *
   * Note: This will become sticky to the bottom of small screens!
   */
  cta?: PageCtaConfig<CtaState>;
  /**
   * Whether or not to hide the search toggle button in the `MenuBar`.
   */
  secondaryCta?: PageCtaConfig<CtaState>;
  /**
   * Secondary CTA button
   */
  hideSearchToggleButton?: boolean;
  /**
   * Metadata for the page.
   *
   * Any props for the `Meta` component are valid here.
   *
   * @see ~components/Meta
   *
   * @example
   * {meta: {title: 'Page title', description: 'Page dsescription'}}
   */
  meta?: MetaProps;
  /**
   * The path for the page.
   *
   * This is usually set automatically.
   */
  path?: string;
  /**
   * The color of the nav bar when the menu is open
   * If undefined, no color is used
   **/
  menuOpenBGColor?: string;
  /**
   * Hide the nav and footer when set to true
   **/
  hideNavAndFooter?: boolean;
}

export type PageConfigFn<
  TContext = Record<string, unknown>,
  /** State added to location state when the Logo is clicked. */
  LogoLinkState = unknown,
  /** State added to location state when the CTA is clicked. */
  CtaState = unknown,
  /** State added to location state when the right nav item is clicked. */
  RightNavState = unknown,
  /** State added to location state when the left nav item is clicked. */
  LeftNavState = unknown,
> = (ctx: {
  pageContext?: TContext;
}) => PageConfig<LogoLinkState, CtaState, RightNavState, LeftNavState>;

type JsonScalar = string | number | boolean | null;
type JsonObject = {
  [key: string]: JsonScalar | JsonObject | JsonArray;
};
type JsonArray = (JsonScalar | JsonObject | JsonArray)[];
type Json = JsonScalar | JsonObject | JsonArray;

/** The Meta props to extract from page data. */
const EXTRACTABLE_META_KEYS = [
  'title',
  'description',
  'canonical',
  'noIndex',
  'source',
  'ogImage',
  'src',
] as const;

type ExtractableMeta = Pick<MetaProps, typeof EXTRACTABLE_META_KEYS[number]>;

/**
 * Extracts `Meta` props from the given page data.
 *
 * If the data has a `meta` field with an object value, that object
 * will be walked, looking for keys that match extractable props.
 * Once it has found values for all of the extractable `Meta` props,
 * _or_ it has walked all of the `meta` data, it returns the extracted props.
 */
const extractMeta = (
  data?: Json,
  meta: ExtractableMeta = {},
  keysToExtract = new Set(EXTRACTABLE_META_KEYS),
): ExtractableMeta => {
  if (!keysToExtract.size) return meta;

  if (Array.isArray(data)) {
    for (const value of data) {
      extractMeta(value, meta, keysToExtract);
      if (!keysToExtract.size) return meta;
    }
  } else if (data && typeof data === 'object') {
    for (const key_ in data) {
      if (!Object.prototype.hasOwnProperty.call(data, key_)) continue;

      const key = key_ as keyof typeof meta;
      const value: JsonArray | JsonObject | JsonScalar = data[key];

      if (typeof value === 'object') {
        extractMeta(value, meta, keysToExtract);
        if (!keysToExtract.size) return meta;
        continue;
      }

      if (!keysToExtract.has(key) && key !== 'src') continue;

      // Handle noIndex as a boolean
      if (key === 'noIndex') {
        invariant(
          typeof value === 'boolean',
          `invalid meta data type ${typeof value} for noIndex`,
        );

        meta[key] = value;
      } else {
        invariant(
          typeof value === 'string',
          `invalid meta data type ${typeof value}`,
        );

        // Assign the value to the meta object
        meta[key] = value;
      }
      // If the key is 'src', assign its value to 'ogImage'
      if (key === 'src') {
        meta['ogImage'] = value as string;
        keysToExtract.delete('ogImage');
      }
      // Remove the key from the set of keys to extract
      keysToExtract.delete(key);
      if (!keysToExtract.size) return meta;
    }
  }

  return meta;
};

export const usePageConfig = <T extends PageConfig = PageConfig>(): T => {
  const pageContext = usePageContext<{ pageConfig?: PageConfig }>();
  const pageData = usePageData<{ meta?: JsonObject } | undefined>();

  return useMemo(() => {
    return {
      ...pageContext.pageConfig,
      meta: {
        ...pageContext.pageConfig?.meta,
        ...extractMeta(pageData?.meta),
      },
    } as T;
  }, [pageContext, pageData?.meta]);
};
